import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ProjectAbout } from "@/types/project"
import { projectService } from "../end-points/project"
import { toast } from "@/hooks/use-toast"
import { IProjectSocial, IUpdateProject } from "@/interfaces/project.interface"

export const useCreateProject = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: ProjectAbout) => projectService.createProject(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Projeto criado",
          description: "Projeto criado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao criar projeto",
          description: "Não foi possível criar o projeto",
          variant: "destructive",
        })
      }
    }
  })
}

export const useUpdateProject = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: IUpdateProject) => projectService.updateProject(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Projeto atualizado",
          description: "Projeto atualizado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao atualizar projeto",
          description: "Não foi possível atualizar o projeto",
          variant: "destructive",
        })
      }
    }
  })
}

export const useUploadImage = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: ({ data }: { data: FormData }) =>
      projectService.uploadImageProject(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Documento enviado",
          description: "Documento enviado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao enviar documento",
          description: "Não foi possível enviar o documento",
          variant: "destructive",
        })
      }
    }
  })
}

export const useGetProjects = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["projects"],
    queryFn: () => projectService.getProjects(),
    enabled: options?.enabled,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  })
}

export const useGetProjectById = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["project", id],
    queryFn: () => projectService.getProjectById(id),
    enabled: enabled && !!id,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  })
}

export const useRegisterSocialProject = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: IProjectSocial) => projectService.registerSocialProject(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Projeto social registrado",
          description: "Projeto social registrado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao registrar projeto social",
          description: "Não foi possível registrar o projeto social",
          variant: "destructive",
        })
      }
    }
  })
}

export const useDeleteProject = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => projectService.deleteProject(id),
    onSuccess: () => {
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["projects"] });

      if (showToast) {
        toast({
          title: "Projeto excluído",
          description: "Projeto excluído com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.log(err);
      if (showToast) {
        toast({
          title: "Erro ao excluir projeto",
          description: "Não foi possível excluir o projeto",
          variant: "destructive",
        });
      }
    }
  });
}
